import { useState, useEffect, useCallback } from "react";
import {
  FaTrophy,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaBolt,
} from "react-icons/fa";
import { fetchLeaderboard } from "../../apis/LeaderboardApis";
import { useAuth } from "../Authentication/AuthContext";
import { SkeletonLeaderboard } from "../Skeletons";
import { useTheme } from "../Theme/ThemeContext";

const LEADERBOARD_TABS = [
  { key: "all", label: "Overall" },
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
];

const ITEMS_PER_PAGE = 5;

// Generate avatar initials from username
function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

// Generate consistent color based on username
function getAvatarColor(name) {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
    "bg-orange-500",
    "bg-emerald-500",
    "bg-rose-500",
    "bg-cyan-500",
    "bg-violet-500",
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Rank badge component
function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <span className="text-sm font-bold text-amber-600">
        1<sup className="text-xs">st</sup>
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="text-sm font-bold text-gray-500">
        2<sup className="text-xs">nd</sup>
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="text-sm font-bold text-orange-600">
        3<sup className="text-xs">rd</sup>
      </span>
    );
  }
  return (
    <span className="text-sm font-semibold text-gray-600">
      {rank}<sup className="text-xs">th</sup>
    </span>
  );
}

// Avatar component
function UserAvatar({ username, size = "md" }) {
  const sizeClasses = {
    sm: "w-8 h-8 text-xs",
    md: "w-10 h-10 text-sm",
    lg: "w-12 h-12 text-base",
  };
  
  return (
    <div
      className={`${sizeClasses[size]} ${getAvatarColor(username)} rounded-full flex items-center justify-center text-white font-semibold shadow-sm`}
    >
      {getInitials(username)}
    </div>
  );
}

// Leaderboard entry row
function LeaderboardRow({ entry, rank, isCurrentUser, themeGradient }) {
  const displayName = entry.username || "Anonymous";
  const truncatedName = displayName.length > 14 
    ? displayName.substring(0, 14) + "..." 
    : displayName;

  return (
    <div
      className={`
        flex items-center gap-3 py-3 px-3 rounded-lg transition-all duration-200
        ${isCurrentUser 
          ? `bg-gradient-to-r ${themeGradient} text-white shadow-lg` 
          : "hover:bg-gray-50 dark:hover:bg-white/5"
        }
      `}
    >
      {/* Rank */}
      <div className="w-8 flex-shrink-0">
        {isCurrentUser ? (
          <span className="text-sm font-bold text-white">-</span>
        ) : (
          <RankBadge rank={rank} />
        )}
      </div>

      {/* Avatar */}
      <UserAvatar username={displayName} size="md" />

      {/* Name */}
      <div className="flex-1 min-w-0">
        <span
          className={`font-medium truncate block ${isCurrentUser ? "text-white" : "text-gray-800 dark:text-gray-200"}`}
          title={displayName}
        >
          {truncatedName}
        </span>
      </div>

      {/* Score */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <FaBolt className={`w-3.5 h-3.5 ${isCurrentUser ? "text-yellow-300" : "text-amber-500"}`} />
        <span className={`font-bold ${isCurrentUser ? "text-white" : "text-gray-900 dark:text-white"}`}>
          {entry.totalScore?.toLocaleString() || 0}
        </span>
      </div>
    </div>
  );
}

// Empty state
function EmptyState({ type }) {
  const labels = { all: "overall", week: "this week", month: "this month" };
  return (
    <div className="py-10 text-center">
      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
        <FaTrophy className="w-5 h-5 text-gray-400" />
      </div>
      <p className="text-sm text-gray-500">
        No rankings {labels[type] || ""} yet
      </p>
    </div>
  );
}

// Main Leaderboard Component
export default function Leaderboard({ onClose }) {
  const [activeTab, setActiveTab] = useState("all");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const { loggedInUser } = useAuth();
  const { currentTheme } = useTheme();
  const currentUserId = loggedInUser?.id || loggedInUser?.userId;

  // Fetch leaderboard
  const loadLeaderboard = useCallback(async (type) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLeaderboard({ type });
      setEntries(data.entries || []);
      setTotalUsers(data.totalUsers || 0);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to load leaderboard:", err);
      setError("Failed to load");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard(activeTab);
  }, [activeTab, loadLeaderboard]);

  // Pagination
  const top10 = entries.slice(0, 10);
  const totalPages = Math.ceil(top10.length / ITEMS_PER_PAGE);
  const paginatedEntries = top10.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Current user entry (if not in top 10)
  const currentUserInTop10 = currentUserId
    ? top10.some((e) => e.userId === currentUserId)
    : false;
  const currentUserEntry = currentUserId
    ? entries.find((e) => e.userId === currentUserId)
    : null;

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  return (
    <div className="glass rounded-2xl shadow-lg border border-white/20 dark:border-white/10 overflow-hidden h-fit sticky top-24">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 dark:border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Leaderboard</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close leaderboard"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-white/10 -mb-4">
          {LEADERBOARD_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                flex-1 py-2.5 text-sm font-medium transition-all relative
                ${activeTab === tab.key
                  ? "text-theme-primary"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                }
              `}
            >
              {tab.label}
              {activeTab === tab.key && (
                <span className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${currentTheme.colors.gradient} rounded-full`} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-3 py-3">
        {loading ? (
          <SkeletonLeaderboard count={5} />
        ) : error ? (
          <div className="py-8 text-center">
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <button
              onClick={() => loadLeaderboard(activeTab)}
              className="text-sm font-medium text-theme-primary hover:underline"
            >
              Retry
            </button>
          </div>
        ) : entries.length === 0 ? (
          <EmptyState type={activeTab} />
        ) : (
          <div className="space-y-1">
            {paginatedEntries.map((entry, idx) => {
              const actualRank = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;
              return (
                <LeaderboardRow
                  key={entry.userId || idx}
                  entry={entry}
                  rank={entry.rank || actualRank}
                  isCurrentUser={entry.userId === currentUserId}
                  themeGradient={currentTheme.colors.gradient}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && entries.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-3 border-t border-gray-100 dark:border-white/10">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FaChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
            {currentPage}/{totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FaChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Current User (if not in top 10) */}
      {!loading && !error && currentUserId && !currentUserInTop10 && currentUserEntry && (
        <div className="px-3 pb-3">
          <LeaderboardRow
            entry={currentUserEntry}
            rank={currentUserEntry.rank}
            isCurrentUser={true}
            themeGradient={currentTheme.colors.gradient}
          />
        </div>
      )}

      {/* Footer message */}
      {!loading && !error && entries.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-white/5 border-t border-gray-100 dark:border-white/10">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {totalUsers > 0 
              ? `${totalUsers} participants • Updated live`
              : "Leaderboard updates live"
            }
          </p>
        </div>
      )}
    </div>
  );
}
